'use client';
import type { TeamInfoSection } from '@baseball-near-you/common';
import { Dot, ListChecks, XIcon } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../utils';
import { RichTextEditor } from '../rich-text-editor';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '../ui';

export function TeamContentEditorAccordion({
  teamSectionData,
  saveSectionData,
}: {
  teamSectionData: Record<TeamInfoSection, string | null>;
  saveSectionData: (
    section: TeamInfoSection,
    data: string | null
  ) => Promise<void>;
}) {
  const [changingSection, setChangingSection] =
    useState<TeamInfoSection | null>(null);

  return (
    <Accordion collapsible className="w-full" type="single">
      {Object.entries(teamSectionData).map(([section, sectionData]) => (
        <AccordionItem
          className="last:border-b-0"
          key={section}
          value={section}
        >
          <AccordionTrigger>
            <div className="flex items-center gap-x-5">
              {section}
              <div
                className={cn(
                  'flex items-center justify-between rounded-sm border px-2 py-1 text-sm',
                  sectionData
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-gray-50 pl-0 text-gray-700'
                )}
              >
                {sectionData ? (
                  <>
                    <ListChecks className="size-4" />
                    <p className="ml-2">Content added</p>
                  </>
                ) : (
                  <>
                    <Dot size={23} />
                    <p>No content</p>
                  </>
                )}
              </div>

              {section === changingSection && (
                <button
                  className="flex h-full items-center justify-between rounded-sm border px-4 py-1 text-sm no-underline hover:no-underline"
                  onClick={() => {
                    setChangingSection(null);
                  }}
                  type="button"
                >
                  discard changes
                  <XIcon size={16} />
                </button>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {sectionData || changingSection === section ? (
              <div className="flex h-full w-full items-center justify-center">
                <RichTextEditor
                  content={sectionData || ''}
                  onSave={async (data) => {
                    await saveSectionData(
                      section as TeamInfoSection,
                      data === '' ? null : data
                    );
                    setChangingSection(null);
                  }}
                />
              </div>
            ) : (
              <Button
                onClick={() => {
                  setChangingSection(section as TeamInfoSection);
                }}
              >
                Add content
              </Button>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
